import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, BookOpen, ArrowRight, CheckCircle, ShoppingCart, Sparkles, Gamepad2, HelpCircle, BookText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/FDoJxork";

// Element tiles configuration
const ELEMENT_TILES = [
  { id: 'element-1', title: 'Element 1: Regulatory Framework', description: 'Regulators, registration, market structure, key laws, AML obligations, and privacy — with 4 interactive games, 12 flashcards, and a 10-question exam-style quiz', icon: '📘', color: '#22d3ee', gradient: 'linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)' },
  { id: 'element-2', title: 'Element 2: Client Relationships', description: 'The CRM framework, KYC deep dive, client classification, Accredited Investor rules, suitability assessment, third parties, and documentation — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: '📗', color: '#34d399', gradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)' },
  { id: 'element-3', title: 'Element 3: KYC/KYP Requirements', description: 'RR vs. IR roles, legal duties, service models, KYP, suitability vs. appropriateness, investment styles, benchmarks, escalation triggers, and foreign jurisdiction rules — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: '📙', color: '#fbbf24', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' },
  { id: 'element-4', title: 'Element 4: Client Complaints', description: 'The mandatory complaint process, regulatory roles, recourse options, misconduct categories, and the private settlement prohibition — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: '📕', color: '#f87171', gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' },
  { id: 'element-5', title: 'Element 5: From Rules to Analysis', description: 'Economic theory & policy, key indicators, business cycle investing, industry analysis, financial ratios, corporate governance, and the three schools of securities analysis — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: '📓', color: '#a78bfa', gradient: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)' },
  { id: 'element-6', title: 'Element 6: Market Integrity & Trade Execution', description: 'UMIR prohibited practices, the gatekeeping obligation, the trade lifecycle (T+1), order types, account types, and reporting to FINTRAC, CIRO, and clients — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: '📔', color: '#f472b6', gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)' },
  { id: 'element-7', title: 'Element 7: Securities, Managed Products & Alternatives', description: 'The complete KYP universe: equities, fixed income, mutual funds, ETFs, REITs, closed-end funds, hedge funds, structured products, and crypto — with 5 games applying the Features → Risks → Tax framework to real client scenarios.', icon: '📒', color: '#818cf8', gradient: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' },
  { id: 'element-8', title: 'Element 8: Derivatives', description: 'Calls, puts, futures, hedging vs. speculation, the naked call\'s unlimited risk, leverage mechanics, and mandatory administrative requirements — with 5 interactive games, 12 flashcards, and a 10-question exam quiz.', icon: null, color: '#2dd4bf', gradient: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)' },
  { id: 'element-9', title: 'Element 9: Ethics & Conduct', description: 'Identifying and managing conflicts (Avoid → Control → Disclose), CIRO\'s ethical standards, personal conduct rules, confidentiality, cybersecurity, and information barriers — with 5 interactive games, 12 flashcards, and a 10-question exam quiz', icon: null, color: '#fb923c', gradient: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' },
];

const CIREInteractiveBook = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false); // Start with false - show sales page first
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const PRICE = 34.99;
  const ORIGINAL_PRICE = 99.99;

  // Check access on mount if token is in URL
  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken, userEmail = '') => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/books/cire-interactive/verify-access?token=${accessToken}&email=${encodeURIComponent(userEmail)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          if (data.first_name) setUserName(data.first_name);
          // Store token in localStorage for individual pages
          localStorage.setItem('cire_access_token', accessToken);
          // Update URL with token
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access credentials. Please check your email and token.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    
    if (!email || !token) {
      setError('Please enter both email and access token.');
      return;
    }
    
    verifyAccess(token, email);
  };

  const handleElementClick = (elementId) => {
    if (!hasAccess) return;
    const url = `/books/cire-interactive/${elementId}?token=${tokenFromUrl || token}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #4f46e5 100%)',
        padding: '80px 20px 100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Link to="/cire-exam" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255,255,255,0.7)',
            textDecoration: 'none',
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            ← Back to CIRE Exam
          </Link>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              boxShadow: '0 10px 40px rgba(212,175,55,0.3)'
            }}>
            </div>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(212,175,55,0.2)',
                border: '1px solid rgba(212,175,55,0.3)',
                borderRadius: '20px',
                padding: '6px 14px',
                marginBottom: '12px'
              }}>
                <Sparkles size={14} style={{ color: '#D4AF37' }} />
                <span style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600' }}>INTERACTIVE EDITION</span>
              </div>
              <h1 style={{
                fontSize: '42px',
                fontWeight: '800',
                color: 'white',
                lineHeight: 1.2,
                marginBottom: '12px'
              }}>
                CIRE Interactive Quick Study Guide
              </h1>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                maxWidth: '600px',
                lineHeight: 1.6,
                marginBottom: '24px'
              }}>
                Master the Canadian Investment Regulatory Examination with games, quizzes, and element-wise short readings. 
                All 9 CIRO elements covered with interactive learning tools.
              </p>
              
              {/* Pricing */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  fontSize: '1.8rem',
                  color: 'rgba(255,255,255,0.4)',
                  textDecoration: 'line-through',
                  fontWeight: 600
                }}>
                  $99.99 CAD
                </span>
                <span style={{
                  fontSize: '2.8rem',
                  color: '#D4AF37',
                  fontWeight: 900,
                  lineHeight: 1
                }}>
                  $34.99 CAD
                </span>
                <div style={{
                  background: 'rgba(212,175,55,0.25)',
                  border: '2px solid rgba(212,175,55,0.5)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '0.9rem',
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  April Only - Save 65%!
                </div>
              </div>
            </div>
          </div>

          {checkingAccess && (
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>Checking access...</div>
          )}
        </div>
      </div>

      {/* Sales Page - Show when no access and access form not shown */}
      {!hasAccess && !showAccessForm && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            marginBottom: '60px',
            flexWrap: 'wrap'
          }}>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(212,175,55,0.4)',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={22} />
              Buy Now - ${PRICE} CAD (Save 65%!)
            </a>
            <button
              onClick={() => setShowAccessForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              }}
            >
              <BookOpen size={22} />
              Already Purchased? Access Here
            </button>
          </div>

          {/* Elements Preview Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element) => (
              <div
                key={element.id}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  opacity: 0.7,
                  position: 'relative'
                }}
              >
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '16px'
                }}>
                  {element.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {element.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {element.description}
                </p>
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(212,175,55,0.2)',
                  border: '1px solid rgba(212,175,55,0.4)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Lock size={14} style={{ color: '#D4AF37' }} />
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#D4AF37',
                    fontWeight: 600
                  }}>
                    Locked
                  </span>
                </div>
              </div>
            ))}
            
            {/* Mock Exam Tile - Locked */}
            <div
              style={{
                background: 'rgba(30,41,59,0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '28px',
                opacity: 0.7,
                position: 'relative'
              }}
            >
              <div style={{
                fontSize: '3rem',
                marginBottom: '16px'
              }}>
                
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'white',
                marginBottom: '12px'
              }}>
                Full Mock Exam & Diagnostic
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.6,
                marginBottom: '16px'
              }}>
                110 exam-style questions with comprehensive diagnostic report • Performance tier analysis • Exam readiness gauge • Targeted study tips • Time analytics
              </p>
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(212,175,55,0.2)',
                border: '1px solid rgba(212,175,55,0.4)',
                borderRadius: '8px',
                padding: '6px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Lock size={14} style={{ color: '#D4AF37' }} />
                <span style={{
                  fontSize: '0.75rem',
                  color: '#D4AF37',
                  fontWeight: 600
                }}>
                  Locked
                </span>
              </div>
            </div>
            
            {/* NEW: CIRE Mock Exam Pack Tile - Separate Product */}
            <div
              onClick={() => window.location.href = '/books/cire-mock-exam-pack'}
              style={{
                background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(139,92,246,0.15) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '2px solid rgba(212,175,55,0.4)',
                padding: '28px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(212,175,55,0.3)',
                border: '1px solid rgba(212,175,55,0.5)',
                borderRadius: '8px',
                padding: '4px 10px',
                fontSize: '0.7rem',
                color: '#D4AF37',
                fontWeight: 700,
                letterSpacing: '0.05em'
              }}>
                NEW
              </div>
              <div style={{
                fontSize: '3rem',
                marginBottom: '16px'
              }}>
                📝
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'white',
                marginBottom: '12px'
              }}>
                CIRE Mock Exam Pack - $49.99
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6,
                marginBottom: '16px'
              }}>
                5 full-length CIRE mock exams • 550 questions with detailed rationales • Practice & exam modes • Performance tracking • Lifetime access
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#D4AF37',
                fontSize: '0.95rem',
                fontWeight: 600
              }}>
                View Mock Exam Pack →
              </div>
            </div>

            {/* NEW: CIRE Element-wise Question Bank Tile */}
            <div
              onClick={() => window.location.href = '/books/cire-element-bank'}
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.15) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '2px solid rgba(139,92,246,0.4)',
                padding: '28px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = 'rgba(139,92,246,0.6)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(139,92,246,0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(139,92,246,0.4)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(139,92,246,0.3)',
                border: '1px solid rgba(139,92,246,0.5)',
                borderRadius: '8px',
                padding: '4px 10px',
                fontSize: '0.7rem',
                color: '#8b5cf6',
                fontWeight: 700,
                letterSpacing: '0.05em'
              }}>
                NEW
              </div>
              <div style={{
                fontSize: '3rem',
                marginBottom: '16px'
              }}>
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'white',
                marginBottom: '12px'
              }}>
                Element-Wise Question Bank - $49.99
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6,
                marginBottom: '16px'
              }}>
                9 element question banks with detailed rationales • Master each CIRE element • Practice & test modes • Performance tracking • Lifetime access
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#8b5cf6',
                fontSize: '0.95rem',
                fontWeight: 600
              }}>
                View Element Question Bank →
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div style={{
            marginTop: '60px',
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '16px'
            }}>
              Ready to Master the CIRE Exam?
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              marginBottom: '32px',
              lineHeight: 1.6
            }}>
              Get lifetime access to all 9 interactive elements - April Special: 65% Off!
            </p>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 40px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                textDecoration: 'none'
              }}
            >
              <ShoppingCart size={22} />
              Get Instant Access - ${PRICE} CAD
            </a>
          </div>
        </div>
      )}

      {/* Access Form - Show when access button clicked */}
      {showAccessForm && !hasAccess && (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 20px' }}>
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            background: 'rgba(30,41,59,0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '48px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem'
              }}>
                🔐
              </div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 800,
                color: 'white',
                marginBottom: '12px'
              }}>
                Access Your Interactive Book
              </h2>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.6
              }}>
                Enter your email and access token to unlock all 9 interactive elements
              </p>
            </div>

            <form onSubmit={handleAccessSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Access Token
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your access token"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#fca5a5',
                  fontSize: '0.9rem',
                  marginBottom: '20px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={checkingAccess}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  border: 'none',
                  background: checkingAccess 
                    ? 'rgba(212,175,55,0.5)' 
                    : 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  color: 'white',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  cursor: checkingAccess ? 'not-allowed' : 'pointer'
                }}
              >
                {checkingAccess ? 'Verifying...' : 'Access Book'}
              </button>
            </form>

            <div style={{
              marginTop: '24px',
              textAlign: 'center',
              paddingTop: '24px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <p style={{
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '12px'
              }}>
                Don't have access yet?
              </p>
              <a
                href={KAJABI_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#D4AF37',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                Purchase the Interactive Book →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Elements Grid - Show when has access */}
      {hasAccess && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element) => (
              <div
                key={element.id}
                onClick={() => handleElementClick(element.id)}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '16px'
                }}>
                  {element.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {element.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {element.description}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#D4AF37',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}>
                  Start Element →
                </div>
              </div>
            ))}
            
            {/* Mock Exam Tile - Accessible */}
            <div
              onClick={() => window.location.href = '/cire-mock-exam'}
              style={{
                background: 'linear-gradient(135deg, rgba(232,160,32,0.15) 0%, rgba(96,165,250,0.15) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '2px solid rgba(232,160,32,0.4)',
                padding: '28px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = 'rgba(232,160,32,0.6)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(232,160,32,0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(232,160,32,0.4)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(232,160,32,0.2)',
                border: '1px solid rgba(232,160,32,0.4)',
                borderRadius: '8px',
                padding: '4px 10px',
                fontSize: '0.7rem',
                color: '#e8a020',
                fontWeight: 700,
                letterSpacing: '0.05em'
              }}>
                DIAGNOSTIC
              </div>
              <div style={{
                fontSize: '3rem',
                marginBottom: '16px'
              }}>
                
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'white',
                marginBottom: '12px'
              }}>
                Full Mock Exam & Diagnostic
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.6,
                marginBottom: '16px'
              }}>
                110 exam-style questions • Comprehensive performance analysis • Exam readiness gauge • Targeted study tips
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#e8a020',
                fontSize: '0.95rem',
                fontWeight: 600
              }}>
                Start Mock Exam →
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CIREInteractiveBook;
